import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  AccountCircle,
  ExitToApp,
  Menu as MenuIcon,
  People,
  HowToVote,
  Assessment,
  Monitor,
  Person,
  AdminPanelSettings,
  Event,
  EmojiEvents,
} from "@mui/icons-material";
import { logout } from "../store/slices/authSlice";
import { authService } from "../services/authService";
import { eventService } from "../services/eventService";
import {
  getEventSongsData,
  getEventMeta,
  isKnownEventKey,
} from "../data/songsData";
import { Chip } from "@mui/material";

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [activeNight, setActiveNight] = React.useState(null);
  const [resultsRevealed, setResultsRevealed] = React.useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const isAdmin = user?.email === "admin@esc.com" || user?.role === "admin";

  const eventKeyFromPath = React.useMemo(() => {
    const segments = location.pathname.split("/").filter(Boolean);
    const candidate = segments[0];
    return candidate && isKnownEventKey(candidate) ? candidate : null;
  }, [location.pathname]);

  const effectiveEventKey = eventKeyFromPath || "sanremo";
  const { nights } = getEventSongsData(effectiveEventKey);

  const eventTitle = React.useMemo(() => {
    if (!eventKeyFromPath) return "LiveVoting";
    return getEventMeta(effectiveEventKey)?.title || effectiveEventKey;
  }, [eventKeyFromPath, effectiveEventKey]);

  React.useEffect(() => {
    const unsubscribe = eventService.subscribeToEvent(
      effectiveEventKey,
      (ev) => {
        const next = Number(ev?.currentSubEventId);
        setActiveNight(Number.isFinite(next) ? next : null);
        setResultsRevealed(Boolean(ev?.resultsRevealed));
      },
      (error) => {
        console.error("Navbar event subscription failed:", error);
        setActiveNight(null);
        setResultsRevealed(false);
      },
    );
    return () => unsubscribe();
  }, [effectiveEventKey]);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDrawerClose = () => {
    setMobileOpen(false);
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
      dispatch(logout());
      navigate("/login");
    } catch (error) {
      console.error("Errore durante il logout:", error);
    }
    handleClose();
  };

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  const handleNavigate = (path) => {
    navigate(path);
    if (isMobile) {
      handleDrawerClose();
    }
  };

  const basePath = `/${effectiveEventKey}`;
  const isEurovision = effectiveEventKey === "eurovision";

  const menuItems = [
    { text: "Eventi", path: "/", icon: <EmojiEvents /> },
    { text: "Artisti", path: `${basePath}`, icon: <People /> },
    { text: "Scaletta", path: `${basePath}/schedule`, icon: <Event /> },
    { text: "Top5", path: `${basePath}/fanta`, icon: <EmojiEvents /> },
    { text: "Vota", path: `${basePath}/voting`, icon: <HowToVote /> },
    { text: "Risultati", path: `${basePath}/results`, icon: <Assessment /> },
    { text: "Monitor", path: `${basePath}/monitor`, icon: <Monitor /> },
    { text: "Profilo", path: "/profile", icon: <Person /> },
  ];

  if (isAdmin) {
    menuItems.push({
      text: "Admin",
      path: `${basePath}/admin`,
      icon: <AdminPanelSettings />,
    });
  }

  const visibleMenuItems = (() => {
    if (!isEurovision) return menuItems;
    const hidden = new Set(["Scaletta", "Top5"]);
    const items = menuItems.filter((item) => !hidden.has(item.text));
    if (!resultsRevealed) {
      return items.filter((item) => item.text !== "Risultati");
    }
    return items;
  })();

  const drawer = (
    <Box sx={{ width: 250 }} role="presentation">
      <Box
        sx={{
          p: 2,
          textAlign: "center",
          bgcolor: "primary.main",
          color: "white",
        }}
      >
        <Typography variant="h6">{eventTitle}</Typography>
        {activeNight && nights[activeNight] && (
          <Chip
            label={nights[activeNight].label}
            color="secondary"
            size="small"
            sx={{ mt: 1, bgcolor: "rgba(255,255,255,0.2)", color: "white" }}
          />
        )}
      </Box>
      <Divider />
      <List>
        {visibleMenuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              onClick={() => handleNavigate(item.path)}
              selected={
                isActive(item.path) ||
                (item.path === basePath && isActive(`${basePath}/artists`))
              }
            >
              <ListItemIcon
                sx={{
                  color:
                    isActive(item.path) ||
                    (item.path === basePath && isActive(`${basePath}/artists`))
                      ? "primary.main"
                      : "inherit",
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                sx={{
                  color:
                    isActive(item.path) ||
                    (item.path === basePath && isActive(`${basePath}/artists`))
                      ? "primary.main"
                      : "inherit",
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={handleLogout}>
            <ListItemIcon>
              <ExitToApp />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItemButton>
        </ListItem>
      </List>
      {(user?.displayName || user?.email) && (
        <Box sx={{ p: 2, textAlign: "center", color: "text.secondary" }}>
          <Typography variant="caption">
            {user.displayName || user.email}
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          {isAuthenticated && isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
            <Typography variant="h6" component="div" sx={{ mr: 2 }}>
              {eventTitle}
            </Typography>
            {activeNight && nights[activeNight] && (
              <Chip
                label={`In corso: ${nights[activeNight].label}`}
                color="secondary"
                variant="filled"
                size="small"
                sx={{
                  fontWeight: "bold",
                  boxShadow: 1,
                  display: { xs: "none", sm: "flex" },
                }}
              />
            )}
          </Box>

          {isAuthenticated && !isMobile && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {visibleMenuItems.map((item) => (
                <Button
                  key={item.text}
                  color="inherit"
                  onClick={() => handleNavigate(item.path)}
                  variant={
                    isActive(item.path) ||
                    (item.path === "/" && isActive("/artists"))
                      ? "outlined"
                      : "text"
                  }
                >
                  {item.text}
                </Button>
              ))}

              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                color="inherit"
              >
                <AccountCircle />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                keepMounted
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                <MenuItem
                  onClick={() => {
                    handleClose();
                    navigate("/profile");
                  }}
                >
                  <Person sx={{ mr: 1 }} />
                  Profilo
                </MenuItem>
                <MenuItem onClick={handleClose}>
                  <Typography variant="body2">
                    {user?.displayName || user?.email}
                  </Typography>
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  <ExitToApp sx={{ mr: 1 }} />
                  Logout
                </MenuItem>
              </Menu>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {isAuthenticated && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerClose}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": { boxSizing: "border-box", width: 250 },
          }}
        >
          {drawer}
        </Drawer>
      )}
    </>
  );
};

export default Navbar;
